import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogTitle,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { getProductGroup } from "../../../api/Product/api";
import { dukaElements } from "../../../api/Duka/api";
import { X } from "lucide-react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const SaleForm = ({ initialData, onSubmit, onCancel }) => {
  const [productGroup, setProductGroup] = useState([]);
  const [dukaName, setDukaName] = useState([]);
  const [product, setProduct] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(
    initialData
      ? {
          ...initialData,
          sales_date: initialData.sales_date ? dayjs(initialData.sales_date) : null,
          product_group: initialData.product_group || '',
          product_code: initialData.product_code || '',
        }
      : {
          location: '',
          duka: '',
          product: '',
          product_group: '',
          product_group_name: '',
          document_type: '',
          product_code: '',
          amount: '',
          sales_date: null,
          netprice: '',
          netprice_total: '',
          productvat: '',
          total_selling_price: '',
        }
  );

  const fetchProductGroup = async () => {
    try {
      const response = await getProductGroup();
      setProductGroup(response.data);
    } catch (error) {
      console.error("Error fetching product group:", error);
      setErrors(prev => ({ ...prev, general: "Failed to load product groups" }));
    }
  };

  const fetchDukaName = async () => {
    try {
      const response = await dukaElements('dukas');
      setDukaName(response.data);
    } catch (error) {
      console.error("Error fetching dukas:", error);
      setErrors(prev => ({ ...prev, general: "Failed to load dukas" }));
    }
  };

  const fetchProductName = async () => {
    try {
      const response = await dukaElements('products');
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      setErrors(prev => ({ ...prev, general: "Failed to load products" }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    if (!formData.duka) {
      newErrors.duka = "Duka is required";
      isValid = false;
    }
    if (!formData.product) {
      newErrors.product = "Product is required";
      isValid = false;
    }
    if (!formData.product_group) {
      newErrors.product_group = "Product group is required";
      isValid = false;
    }
    if (!formData.product_code) {
      newErrors.product_code = "Product code is required";
      isValid = false;
    }
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = "Amount must be greater than 0";
      isValid = false;
    }
    if (!formData.sales_date) {
      newErrors.sales_date = "Sale date is required";
      isValid = false;
    }
    if (!formData.netprice || formData.netprice < 0) {
      newErrors.netprice = "Net price must be 0 or greater";
      isValid = false;
    }
    if (!formData.netprice_total || formData.netprice_total < 0) {
      newErrors.netprice_total = "Net total must be 0 or greater";
      isValid = false;
    }
    if (!formData.productvat || formData.productvat < 0) {
      newErrors.productvat = "Row VAT must be 0 or greater";
      isValid = false;
    }
    if (!formData.total_selling_price || formData.total_selling_price < 0) {
      newErrors.total_selling_price = "Row total must be 0 or greater";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === "product" && value) {
      const selectedProduct = product.find(p => p.id === parseInt(value));
      if (selectedProduct) {
        const selectedGroup = productGroup.find(g => g.id === selectedProduct.product_group);
        setFormData(prev => ({
          ...prev,
          product: value,
          product_group: selectedProduct.product_group || '',
          product_group_name: selectedGroup ? selectedGroup.name : '',
          product_code: selectedProduct.code || '',
        }));
      }
    } else if (name === "product" && !value) {
      setFormData(prev => ({
        ...prev,
        product: '',
        product_group: '',
        product_group_name: '',
        product_code: '',
      }));
    }

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        ...formData,
        sales_date: formData.sales_date 
          ? formData.sales_date.format('YYYY-MM-DD') 
          : null,
      };
      onSubmit(payload);
    }
  };

  const handleDateChange = (field, newValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: newValue,
    }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  useEffect(() => {
    fetchProductGroup();
    fetchDukaName();
    fetchProductName();
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          pb: 3,
          fontSize: 18,
          fontWeight: 500,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {initialData ? "Edit Sale" : "Add New Sale"}
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={5}>
          {errors.general && (
            <Grid item xs={12}>
              <FormHelperText error>{errors.general}</FormHelperText>
            </Grid>
          )}
          {!initialData ? 
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required error={!!errors.duka}>
                <InputLabel id="duka-name-label">Duka</InputLabel>
                <Select
                  labelId="duka-name-label"
                  name="duka"
                  value={formData.duka}
                  label="Duka"
                  onChange={handleChange}
                >
                  {dukaName.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.duka && <FormHelperText>{errors.duka}</FormHelperText>}
              </FormControl>
            </Grid>
            :
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label={initialData ? "" : "Duka"}
                name="location"
                value={formData.location}
                disabled={!!initialData}
                required={!initialData}
                onChange={handleChange}
              />
            </Grid>
          }
          {!initialData ? 
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required error={!!errors.product}>
                <InputLabel id="product-name-label">Product Name</InputLabel>
                <Select
                  labelId="product-name-label"
                  name="product"
                  value={formData.product}
                  label="Product Name"
                  onChange={handleChange}
                >
                  {product.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.product && <FormHelperText>{errors.product}</FormHelperText>}
              </FormControl>
            </Grid>
            :
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label={initialData ? "" : "Product"}
                name="product_name"
                value={formData.product_name}
                disabled={!!initialData}
                required={!initialData}
                onChange={handleChange}
              />
            </Grid>
          }
          {!initialData ? 
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth required error={!!errors.product_group}>
                <InputLabel id="product-group-label">Product Group</InputLabel>
                <Select
                  labelId="product-group-label"
                  name="product_group"
                  value={formData.product_group}
                  label="Product Group"
                  onChange={handleChange}
                  disabled={!formData.product}
                >
                  {productGroup.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.product_group && <FormHelperText>{errors.product_group}</FormHelperText>}
              </FormControl>
            </Grid>
            :
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label=""
                name="product_group_name"
                value={formData.product_group_name}
                disabled
              />
            </Grid> 
          }
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Document Type"
              name="document_type"
              value={formData.document_type}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Product Code"
              name="product_code"
              required
              value={formData.product_code}
              onChange={handleChange}
              disabled={!formData.product}
              error={!!errors.product_code}
              helperText={errors.product_code}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Amount"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              required
              size="medium"
              error={!!errors.amount}
              helperText={errors.amount}
              type="number"
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Sale Date"
                value={formData.sales_date}
                onChange={(newValue) => handleDateChange('sales_date', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true,
                    error: !!errors.sales_date,
                    helperText: errors.sales_date,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Net Price (KES)"
              name="netprice"
              value={formData.netprice}
              onChange={handleChange}
              required
              size="medium"
              error={!!errors.netprice}
              helperText={errors.netprice}
              type="number"
              InputProps={{ inputProps: { min: 0, step: "0.01" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Net Total (KES)"
              name="netprice_total"
              value={formData.netprice_total}
              onChange={handleChange}
              required
              size="medium"
              error={!!errors.netprice_total}
              helperText={errors.netprice_total}
              type="number"
              InputProps={{ inputProps: { min: 0, step: "0.01" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Row Vat (KES)"
              name="productvat"
              value={formData.productvat}
              onChange={handleChange}
              required
              size="medium"
              error={!!errors.productvat}
              helperText={errors.productvat}
              type="number"
              InputProps={{ inputProps: { min: 0, step: "0.01" } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Row Total (KES)"
              name="total_selling_price"
              value={formData.total_selling_price}
              onChange={handleChange}
              required
              size="medium"
              error={!!errors.total_selling_price}
              helperText={errors.total_selling_price}
              type="number"
              InputProps={{ inputProps: { min: 0, step: "0.01" } }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: "#e0e0e0",
                  color: "#000",
                  "&:hover": { bgcolor: "#d5d5d5" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  bgcolor: "#00694B",
                  color: "#fff",
                  "&:hover": { bgcolor: "#005039" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                {initialData ? "Update" : "Create"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default SaleForm;